import React, { useEffect, useState, useMemo, useContext, useCallback, useRef } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Carousel, Glyphicon } from '@bingads-webui-react/primitive-unthemed';
import { TooltipHost, DirectionalHint } from '@bingads-webui-react/fabric-control';
import { curlyTemplate, generateGuid } from '@bingads-webui-universal/primitive-utilities';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import {
  withTrack,
  withGetSummary,
  getSummaryCardModel,
  isReadOnly,
  ADINSIGHT_LOG_API_NAME,
  RECOMMENDATION_IDS,
  ADINSIGHT_LOG_ACTION_TYPE,
  NOT_SUPPORT_DOWNLOAD_TYPES,
  DOWNLOAD_SCOPE,
  APPLY_ACTION_TYPES,
  AUTO_APPLY_TYPES,
  SUPPORT_ADINSIGHT_AUTO_APPLY_TYPES,
} from '@bingads-webui-campaign/recommendation-core';
import { SummaryFooter, SummaryHeader } from '@bingads-webui-campaign-react/recommendation-summary-view';
import { ApplyFormModal } from '@bingads-webui-campaign-react/recommendation-modal';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';

import { getStyles } from './card-style';

const SummaryFooterWrapper = withTrack(/* withAll */ true)(SummaryFooter);

const getPathNamePrefix = (isMCC) => {
  const pathNamePrefix = '/campaign/vnext/recommendations/';
  const pathNamePrefixMCC = '/campaign/vnext/accounts/recommendations/';

  return isMCC ? pathNamePrefixMCC : pathNamePrefix;
};

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function InContextCardBase(props) {
  const {
    isMCC,
    guid,
    setInContextRecommendationContent,
    classes,
    data,
    deps,
    scope,
    currentActivity,
    adDisplayUrlService,
    dataService,
    newI18n,
    campaignType,
    filterService,
    channel,
    hideCard,
    entityType,
    reload,
    applyAllCallback,
    campaignAdGroups,
    haveEnoughSpace,
    overallOptimizationScoreBar,
    perfMarker,
  } = props;
  const scopeService = useContext(ScopeContext);
  const dataFiltered = useMemo(() => {
    const filtered = _.map(data, item => item);
    return filtered;
  }, [data]);

  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(null);
  const [isAsyncApplyModalOpen, setIsAsyncApplyModalOpen] = useState(false);
  const [isApplyConfirmModalOpen, setIsApplyConfirmModalOpen] = useState(false);
  const [recommendationCount, setRecommendationCount] = useState(0);
  const [pollTask, setPollTask] = useState(null);
  const [onApplyConfirm, setOnApplyConfirm] = useState(() => _.noop);
  const [onApplyReject, setOnApplyReject] = useState(() => _.noop);

  const readOnly = isReadOnly && isReadOnly(deps.appConfig, isMCC);
  const shouldRenderInContextRecommendationCard = !_.isEmpty(dataFiltered) && haveEnoughSpace;

  const cardContext = useMemo(() => ({
    Source: null,
    Level: scope.levelAt,
    Category: null,
    entityType,
    CampaignIds: scope.campaignId ? [Number(scope.campaignId)] : [],
    AdGroupIds: scope.adGroupId ? [Number(scope.adGroupId)] : [],
  }), [entityType, scope.adGroupId, scope.campaignId, scope.levelAt]);

  const logOpportunityEvent = useCallback(({
    type = null,
    action,
    context = null,
  }) => {
    const api = ADINSIGHT_LOG_API_NAME.INCONTEXTCARD;

    currentActivity.trace({
      type: type || '',
      typeId: RECOMMENDATION_IDS[type],
      action,
      campaignId: scope.campaignId,
      channel,
      position: index + 1,
      guid: guid || '',
      context: JSON.stringify(context),
      entityType,
    }, api);
  }, [channel, currentActivity, entityType, guid, index, scope.campaignId]);

  useEffect(() => {
    setInContextRecommendationContent(!_.isEmpty(dataFiltered));
  }, [dataFiltered, setInContextRecommendationContent]);

  const previousIndex = usePrevious(index);
  const previousType = usePrevious(_.size(dataFiltered) > index ? dataFiltered[index].type : '');

  useEffect(() => {
    if (_.size(dataFiltered) > index &&
      previousIndex !== index &&
      previousType !== dataFiltered[index].type) {
      logOpportunityEvent({
        type: dataFiltered[index].type,
        action: ADINSIGHT_LOG_ACTION_TYPE.IMPRESSION,
        context: cardContext,
      });
    }
  }, [cardContext, dataFiltered, index, logOpportunityEvent, previousIndex, previousType]);

  const showAsyncApplyModal = (poll) => {
    setPollTask(poll);
    setIsAsyncApplyModalOpen(true);
  };

  const closeAsyncApplyModal = useCallback(() => {
    if (pollTask) {
      pollTask.stop();
    }
    setIsAsyncApplyModalOpen(false);
  }, [pollTask]);

  const showApplyConfirmModal = ({ onApply, countRecommendation, onReject = _.noop }) => {
    _.defer(() => {
      setIsApplyConfirmModalOpen(true);
      setRecommendationCount(countRecommendation);
    });
    setOnApplyConfirm(() => onApply);
    setOnApplyReject(() => onReject);
  };

  const closeApplyConfirmModal = () => {
    setIsApplyConfirmModalOpen(false);
  };

  const confirmApplyConfirmModal = useCallback(() => {
    if (_.isFunction(onApplyConfirm)) {
      onApplyConfirm();
    }
    closeApplyConfirmModal();
  }, [onApplyConfirm]);

  const rejectApplyConfirmModal = useCallback(() => {
    if (_.isFunction(onApplyReject)) {
      onApplyReject();
    }
    closeApplyConfirmModal();
  }, [onApplyReject]);

  const handleSelect = useCallback((selectedIndex, e) => {
    if ((selectedIndex === dataFiltered.length && e.direction === 'next')) {
      return;
    }

    setIndex(selectedIndex % dataFiltered.length);
    setDirection(e.direction);
  }, [dataFiltered.length]);

  const handleDownload = useCallback((type, version) => {
    dataService.download({
      channel,
      campaignType,
      filterService,
      recommendationType: type,
    }).then(() => {
      logOpportunityEvent({
        type,
        action: ADINSIGHT_LOG_ACTION_TYPE.DOWNLOAD,
        context: _.extend({
          Version: version,
          DownloadScope: DOWNLOAD_SCOPE.INDIVIDUAL,
        }, cardContext),
      });
    });
  }, [campaignType, cardContext, channel, dataService, filterService, logOpportunityEvent]);

  const renderPitch = useCallback(pitching => (
    pitching ? (
      <TooltipHost
        content={pitching}
        directionalHint={DirectionalHint.rightCenter}
        calloutProps={{ calloutMaxWidth: 200 }}
      >
        <div className={classes.tooTip}>{newI18n.getString(_TL_('See why this is recommended for you'))}</div>
      </TooltipHost>) : null
  ), [classes.tooTip, newI18n]);

  const renderPagination = useMemo(() => (
    _.size(dataFiltered) > 1 &&
      <div className={classes.carouselIndex}>
        {curlyTemplate(newI18n.getString(_TL_('{{pageNumber}} of {{pageCount}}')))({
          pageNumber: index + 1,
          pageCount: _.size(dataFiltered),
        })}
      </div>
  ), [classes.carouselIndex, dataFiltered, index, newI18n]);

  const renderPrevIcon = useMemo(() => (
    _.size(dataFiltered) > 1 &&
    <Glyphicon glyph="ba-ChevronLeft" className={index === 0 ? 'disabled' : ''} />
  ), [dataFiltered, index]);

  const renderNextIcon = useMemo(() => (
    _.size(dataFiltered) > 1 &&
    <Glyphicon glyph="ba-ChevronRight" className={index === dataFiltered.length - 1 ? 'disabled' : ''} />
  ), [dataFiltered, index]);

  const viewDetails = useCallback(type => () => {
    scopeService.scope.set({
      ...scopeService.scope.values,
      recommendationType: type,
      recsrc: channel,
    });
    scopeService.scope.save(`${getPathNamePrefix(isMCC)}All`);
  }, [channel, isMCC, scopeService.scope]);

  const viewHistory = useCallback(() => {
    const url = `${getPathNamePrefix(isMCC)}history?${
      scopeService.scope.stringify(scopeService.scope.values)}`;
    window.open(url);
  }, [isMCC, scopeService.scope]);

  const getAutoApplyToolTipText = useCallback((type) => {
    let recommendationName = '';
    if (type === 'MultiMediaAdsOpportunity') {
      recommendationName = newI18n.getString(_TL_('Multimedia ads'));
    } else if (type === 'ResponsiveSearchAdsOpportunity') {
      recommendationName = newI18n.getString(_TL_('responsive search ads'));
    }

    return curlyTemplate(newI18n.getString(_TL_('New {{recommendationName}} recommendations will auto apply soon.')))({
      recommendationName,
    });
  }, [newI18n]);

  const carousel = useMemo(() => (
    <Carousel
      key={dataFiltered.length}
      activeIndex={index > dataFiltered.length - 1 ? 0 : index}
      direction={dataFiltered.length === 1 ? null : direction}
      onSelect={handleSelect}
      controls={false}
      indicators={false}
      prevIcon={renderPrevIcon}
      nextIcon={renderNextIcon}
      showPrevNextControls={dataFiltered.length > 1}
    > { _.map(dataFiltered, (recommendation) => {
      const model = getSummaryCardModel(_.extend({}, recommendation, {
        i18n: deps.i18n,
        newI18n,
        currency: deps.pageContext.AccountCurrencyCode,
        scope: scope.levelAt,
        permissions: deps.pageContext.Permissions,
        adDisplayUrlService,
        combineText: deps.combineText,
        currentActivity,
      }));

     const headerMenuItems = _.compact([
       _.contains(NOT_SUPPORT_DOWNLOAD_TYPES, model.type) ? null :
      {
        name: newI18n.getString(_TL_('Download this recommendation')),
        func: () => handleDownload(model.type, recommendation.version),
      }, {
      name: newI18n.getString(_TL_('Hide this tile')),
      func: () => {
        logOpportunityEvent({
          action: ADINSIGHT_LOG_ACTION_TYPE.HIDE,
          channel,
          campaignId: scope.campaignId,
          context: cardContext,
        });
        hideCard();
      },
      }]);

      const isOptimizationScoreOn = !isMCC && _.result(deps.pageContext.Permissions.dynamic, 'OptimizationScore');
      const isAutoApplyRecommendationEnabled = !isMCC && _.result(deps.pageContext.Permissions, 'IsAutoApplyRecommendationEnabled');
      const showAutoApply = isAutoApplyRecommendationEnabled
        && _.contains(
            _.keys(AUTO_APPLY_TYPES).concat(SUPPORT_ADINSIGHT_AUTO_APPLY_TYPES),
            model.type
          )
          && recommendation.isAutoApplyOptIn;

      return (
        <Carousel.Item>
          <SummaryHeader
            scope={scope}
            i18n={deps.i18n}
            {...model.summary}
            readOnly={readOnly}
            headerMenuItems={headerMenuItems}
            autoApplyToolTipText={getAutoApplyToolTipText(model.type)}
            showAutoApply={showAutoApply}
            odata={deps.odata}
            inContextCard
            cid={scope.customerId}
            aid={scope.accountId}
            showIntermediateView
            permissions={deps.pageContext.Permissions}
            optimizationScore={recommendation.optimizationScore}
            overallOptimizationScoreBar={overallOptimizationScoreBar}
            isOptimizationScoreOn={isOptimizationScoreOn}
            newI18n={newI18n}
          />
          <div className={classes.cardContent}>
            <div className={classes.description}>
              {model.summary.description}
            </div>
            {renderPitch(model.summary.pitching)}
          </div>
          {renderPagination}
          <SummaryFooterWrapper
            readOnly={readOnly}
            {...model}
            {...recommendation}
            {...deps}
            guid={guid}
            scope={scope}
            context={cardContext}
            campaignAdGroups={campaignAdGroups}
            currentActivity={currentActivity}
            logOpportunityEvent={logOpportunityEvent}
            viewDetails={viewDetails}
            dataService={dataService}
            showApplyConfirmModal={showApplyConfirmModal}
            showAsyncApplyModal={showAsyncApplyModal}
            closeAsyncApplyModal={closeAsyncApplyModal}
            inContextCard
            callback={(op) => {
              reload({
                isFromApplyOrDismiss: op.userAction !== APPLY_ACTION_TYPES.UNDISMISS,
              });
              if (op.userAction === APPLY_ACTION_TYPES.ACCEPT) {
                applyAllCallback();
              }
            }}
            newI18n={newI18n}
          />
        </Carousel.Item>
      );
      })}
      <ApplyFormModal
        i18n={deps.i18n}
        viewHistory={viewHistory}
        isApplyConfirmModalOpen={isApplyConfirmModalOpen}
        isAsyncApplyModalOpen={isAsyncApplyModalOpen}
        countRecommendation={recommendationCount}
        confirmApplyConfirmModal={confirmApplyConfirmModal}
        rejectApplyConfirmModal={rejectApplyConfirmModal}
        closeAsyncApplyModal={closeAsyncApplyModal}
        inContextCard
      />
    </Carousel>
  ), [
    dataFiltered, index, direction, handleSelect,
    renderPrevIcon, renderNextIcon, deps, viewHistory,
    isApplyConfirmModalOpen, isAsyncApplyModalOpen, recommendationCount,
    confirmApplyConfirmModal, rejectApplyConfirmModal, closeAsyncApplyModal,
    scope, adDisplayUrlService, currentActivity, newI18n, isMCC,
    readOnly, getAutoApplyToolTipText, overallOptimizationScoreBar,
    classes.cardContent, classes.description, renderPitch, renderPagination,
    guid, cardContext, campaignAdGroups, logOpportunityEvent, viewDetails,
    dataService, handleDownload, channel, hideCard, reload, applyAllCallback]);

  useEffect(() => {
    if (shouldRenderInContextRecommendationCard && perfMarker && !perfMarker.isDone) {
      perfMarker.done();
    }
  }, [shouldRenderInContextRecommendationCard, perfMarker]);

  return shouldRenderInContextRecommendationCard && (
    <div className={classNames('in-context-recommendation-card', classes.carouselContainer)} >
      {carousel}
    </div>);
}

InContextCardBase.propTypes = {
  setInContextRecommendationContent: PropTypes.func.isRequired,
  classes: PropTypes.shape({}).isRequired,
  isMCC: PropTypes.bool.isRequired,
  guid: PropTypes.string,
  data: PropTypes.shape().isRequired,
  deps: PropTypes.shape().isRequired,
  scope: PropTypes.shape({
    campaignId: PropTypes.number,
    adGroupId: PropTypes.number,
  }).isRequired,
  adDisplayUrlService: PropTypes.shape().isRequired,
  dataService: PropTypes.shape({
    track: PropTypes.func,
  }).isRequired,
  currentActivity: PropTypes.shape({
    trace: PropTypes.func,
  }).isRequired,
  newI18n: PropTypes.shape({}).isRequired,
  downloadService: PropTypes.shape({}).isRequired,
  campaignType: PropTypes.number,
  filterService: PropTypes.shape({}),
  channel: PropTypes.string.isRequired,
  hideCard: PropTypes.func.isRequired,
  entityType: PropTypes.string.isRequired,
  reload: PropTypes.func.isRequired,
  applyAllCallback: PropTypes.func,
  campaignAdGroups: PropTypes.arrayOf(PropTypes.shape({
    Key: PropTypes.number,
    Value: PropTypes.arrayOf(PropTypes.number),
  })).isRequired,
  haveEnoughSpace: PropTypes.bool,
  overallOptimizationScoreBar: PropTypes.shape({
    Gained: PropTypes.number,
    Dismissed: PropTypes.number,
    Available: PropTypes.number,
  }),
  perfMarker: perfMarkerPropTypes,
};

InContextCardBase.defaultProps = {
  guid: generateGuid(),
  campaignType: null,
  filterService: null,
  applyAllCallback: _.noop,
  haveEnoughSpace: true,
  overallOptimizationScoreBar: null,
  perfMarker: null,
};

export const InContextCard = withGetSummary(
  false, // withDetails
  false // needExamples
)(withDefaultStyles(InContextCardBase, getStyles));
